import axios from 'axios';
import tokenService from 'src/services/token/TokenService';
import BaseAPIService from '../base/base-api-service';
import CartModel from 'src/model/cart.model';
import CartItemModel from 'src/model/cartItem.model';
import OneTimeAddressModel from 'src/model/oneTimeAddress.model';
import AddressModel from 'src/model/address.model';
import * as normalizeUrl from 'normalize-url';
import { listsActions } from 'src/store/modules/lists';

export interface ICartServiceItem {
  claims: string;
  delivery?: string;
  deliveryLocation?: string;
  partNumber: string;
  quantity: number;
  rushOrder: boolean;
  shipmentMarking: string;
  shipsfrom: string;
  ticketNumber: string;
  warrantyProvider: string;
}

class CartService extends BaseAPIService<CartModel> {
  private endpoint: string = '/cartapp/v1';
  private endpointNew: string = '/accountapp/v1';

  private ITEM_COUNT_ENDPOINT: string = '/info/itemCount';
  private CART_SHIPPING_ADDRESS: string = '/info/shipping/address';
  private CART_BASIC_INFO_ENDPOINT: string = '/info/basic';
  private CART_ENDPOINT: string = '/info';
  private CLEAR_CART_ENDPOINT: string = '/operations/clear';
  private SWITCH_DELIVER_METHOD_ENDPOINT = '/operations/switch';
  private SWITCH_SHIP_FROM_ENDPOINT = '/operations/switch/shipsfrom';
  private ADD_ITEM_ENDPOINT: string = '/item/add';
  private ADD_ITEMS_ENDPOINT: string = '/item/add/multi';
  private UPDATE_ITEM_ENDPOINT: string = '/item/update';
  private UPLOAD_FILE_ENDPOINT: string = '/item/add/file';
  private DELETE_ITEM_ENDPOINT: string = '/item/delete/';
  private ADD_ITEM_SIMPLE_ENDPOINT: string = '/item/add/simple';
  private ADD_ITEM_FILE_ENDPOINT: string = '/item/add/file';
  private ORDER_PDF_DOWNLOAD: string = '/orders/orderConfirmation/generatepdf';

  constructor() {
    super(CartModel);
  }

  public getCart(): Promise<CartModel> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.CART_ENDPOINT}`
    })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public getCartBasicInfo(): Promise<CartModel> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.CART_BASIC_INFO_ENDPOINT}`
    })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        throw error;
      });
  }

  public getCartItemsCount(): Promise<number> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.ITEM_COUNT_ENDPOINT}`
    })
      .then(response => {
        return response.data.count;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public getCartShippingAddress(): Promise<{
    deliveryLocation: AddressModel;
    code: string;
  }> {
    return this.doRequest({
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.CART_SHIPPING_ADDRESS}`
    })
      .then(response => {
        return response.data;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public addItems(items: ICartServiceItem[]) {
    return this.doRequest({
      baseURL: this.baseURL,
      data: items,
      method: 'post',
      url: `${this.endpoint}${this.ADD_ITEMS_ENDPOINT}`
    }).then(res => {
      return CartItemModel.castArray<CartItemModel>(res.data, CartItemModel);
    });
  }

  public addItem(
    claims: string,
    delivery: string,
    deliveryLocation: string,
    oneTimeAddress: OneTimeAddressModel,
    partNumber: string,
    quantity: number,
    rushOrder: boolean,
    shipmentMarking: string,
    shipsfrom: string,
    ticketNumber: string,
    warrantyProvider: string
  ) {
    const data = {
      claims,
      delivery,
      deliveryLocation,
      oneTimeAddress,
      partNumber,
      quantity,
      rushOrder,
      shipmentMarking,
      shipsfrom,
      ticketNumber,
      warrantyProvider
    };
    return this.doRequest({
      baseURL: this.baseURL,
      data,
      method: 'post',
      url: `${this.endpoint}${this.ADD_ITEM_ENDPOINT}`
    }).then(res => {
      return CartItemModel.cast<CartItemModel>(res.data, CartItemModel);
    });
  }

  public addItemSimple(
    partNumber: string,
    quantity: number
  ): Promise<CartItemModel> {
    return this.doRequest({
      baseURL: this.baseURL,
      data: {
        partNumber,
        quantity
      },
      method: 'post',
      url: `${this.endpoint}${this.ADD_ITEM_SIMPLE_ENDPOINT}`
    }).then(res => {
      return CartItemModel.cast<CartItemModel>(res.data, CartItemModel);
    });
  }

  public deleteItem(itemId: number): Promise<string> {
    return this.doRequest({
      method: 'DELETE',
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.DELETE_ITEM_ENDPOINT}${itemId}`
    })
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public updateItem(
    claims: string,
    id: number,
    quantity: number,
    shipmentMarking: string,
    shipsfrom: string,
    warrantyProvider: string,
    rushOrder: boolean,
    ticketNumber: string
  ) {
    const data = {
      claims,
      id,
      quantity,
      shipmentMarking,
      shipsfrom,
      warrantyProvider,
      rushOrder,
      ticketNumber
    };
    return this.doRequest({
      baseURL: this.baseURL,
      data,
      method: 'put',
      url: `${this.endpoint}${this.UPDATE_ITEM_ENDPOINT}`
    }).then(res => {
      return res;
    });
  }

  public clearCart(): Promise<string> {
    return this.doRequest({
      method: 'DELETE',
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.CLEAR_CART_ENDPOINT}`
    })
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public async uploadFile(file: FormData): Promise<any> {
    const headers = await tokenService
      .getAccessToken()
      .then(token => {
        if (token !== null) {
          // Only add token if it is valid
          return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          };
        } else {
          return {
            'Content-Type': 'multipart/form-data'
          };
        }
      })
      .catch(error => {
        throw error;
      });
    return axios
      .post(
        normalizeUrl(
          `${this.baseURL}${this.endpoint}${this.UPLOAD_FILE_ENDPOINT}`
        ),
        file,
        {
          headers
        }
      )
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public switchDeliverMethod(
    delivery: string,
    deliveryLocation: string,
    oneTimeAddress: OneTimeAddressModel | null
  ): Promise<string> {
    return this.doRequest({
      data: { delivery, deliveryLocation, oneTimeAddress },
      method: 'PUT',
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.SWITCH_DELIVER_METHOD_ENDPOINT}`
    })
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public switchShipFrom(deliveryLocation: string): Promise<string> {
    return this.doRequest({
      data: { deliveryLocation },
      method: 'PUT',
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.SWITCH_SHIP_FROM_ENDPOINT}`
    })
      .then(response => {
        return response;
      })
      .catch(error => {
        console.error(error);
        throw error;
      });
  }

  public generateOrderPDF(orderData: string): Promise<string> {
    const date = new Date(Date.now()).toLocaleString();
    const orderDate = date.split(',')[0];
    return this.doRequest({
      data: { orderData, orderDate },
      method: 'POST',
      baseURL: this.baseURL,
      url: `${this.endpoint}${this.ORDER_PDF_DOWNLOAD}`,
      responseType: 'blob'
    }).then(res => res.data);
  }
}

const cartServiceInstance = new CartService();
Object.freeze(cartServiceInstance);

export default cartServiceInstance;
